import { loyaltyApi } from '@vmos2/core';
import type { Promotion, UserPromotion, UserPromotions } from '@vmos2/loyalty/src/types';
import { useRequest } from '@vmos2/utils';
import { useCallback } from 'react';

import { USER_REWARDS_CACHE_ID } from '../constants';
import { useSettings } from '../Settings';

type UserRewardsResponse = (Exclude<UserPromotion['userReward'], undefined> & {
  promotion: Promotion;
})[];

export const useUserRewards = (kioskUser?: { uuid: string }) => {
  const { loyaltyRewards } = useSettings();
  const cacheId = `${USER_REWARDS_CACHE_ID}${kioskUser?.uuid ? `/${kioskUser.uuid}` : ''}`;
  const apiCache = loyaltyApi.storage.data[cacheId];

  const [{ data, isLoading }, fetchUserRewards, resetRequest] = useRequest<UserPromotions>(
    useCallback(
      async ({ apiCacheData, cache }) => {
        if (loyaltyRewards?.enabled) {
          const response: UserRewardsResponse =
            apiCacheData?.payload ||
            (await loyaltyApi.get('rewards/user', {
              cache,
              id: cacheId,
              params: { userUUID: kioskUser?.uuid },
            }));

          return response.map(({ promotion, ...userReward }) => ({
            ...promotion,
            userReward,
          }));
        }

        return null;
      },
      [cacheId, kioskUser?.uuid, loyaltyRewards?.enabled]
    ),
    // 3600000 = 1 hour
    { apiCache, defaultCache: { override: true, ttl: 3600000 }, preserveDataWhileLoading: true }
  );

  const clearUserRewards = useCallback(async () => {
    resetRequest();
    await loyaltyApi.storage.remove(cacheId);
  }, [cacheId, resetRequest]);

  return {
    clearUserRewards,
    fetchUserRewards,
    isLoadingUserRewards: loyaltyRewards?.enabled ? isLoading : false,
    userRewards: data,
  };
};
